import { isAxiosError } from "axios";
import { useEffect } from "react";

import { ConfirmDialog, FormError } from "@/components/kit";
import { useBulkDeleteProducts } from "@/hooks/api/product/useDeleteProducts";
import { Product } from "@/types";

interface ProductDeleteModalProps {
  isOpen: boolean;
  products: Array<Product>;
  onClose: () => void;
}

export const ProductDeleteModal = ({
  products,
  isOpen,
  onClose,
}: ProductDeleteModalProps) => {
  const {
    mutate: bulkDelete,
    isError,
    error,
    isPending,
    reset,
  } = useBulkDeleteProducts();

  const handleDelete = () => {
    bulkDelete(
      products.map((product) => product.id),
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data.message
        ? error.response?.data.message
        : JSON.stringify(error.response?.data)
      : JSON.stringify(error)
    : null;

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [reset, isOpen]);

  return (
    <ConfirmDialog
      cancelLabel="No, Cancel"
      confirmLabel="Yes, Delete"
      isOpen={isOpen}
      isPending={isPending}
      title={`Delete ${products.length} products?`}
      onClose={onClose}
      onConfirm={handleDelete}
    >
      <div className="flex flex-col gap-3">
        <FormError
          message={errorMessage ? `Failed to delete: ${errorMessage}` : null}
        />
        <p className="text-muted text-sm">
          You&apos;re about to delete {products.length} products. This
          can&apos;t be undone.
        </p>
        <ul className="border-separator flex max-h-64 flex-col divide-y overflow-y-auto rounded-xl border">
          {products.map((product) => (
            <li key={product.id} className="px-3 py-2 text-sm">
              {product.name} (AED: {product.price}) (External ID:{" "}
              {product.externalId})
            </li>
          ))}
        </ul>
      </div>
    </ConfirmDialog>
  );
};
